import requestDefault from '@/shared/utils/request';
import type {
  HomeSummaryParams,
  HomeSummaryResponse,
  UseEntranceInfoParams,
  UseEntranceInfoResponse,
  EntranceUrlResponse,
  CreditMallApplyResponse,
  QueryCreditApplyStatusParams,
  QueryCreditApplyStatusResponse,
  QueryAgreementReviewShowResponse,
  QueryContractListParams,
  QueryContractListResponse,
  WithdrawResultSummaryResponse,
  QueryMultiContractListParams,
  QueryMultiContractListResponse,
} from './types';

/**
 * 首页入口概要信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405663
 * @description 如果data为null，则不需要展示对应入口信息，异常需要捕获处理不展示入口
 * @param params 请求参数（无参数）
 * @returns Promise<HomeSummaryResponse | undefined>
 */
export const queryHomeSummary = async (
  params: HomeSummaryParams = {},
): Promise<HomeSummaryResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryHomeSummary',
  params,
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 使用入口信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405735
 * @description 分期商城使用入口信息查询接口，如果data为null，则不需要展示对应入口信息，异常需要捕获处理不展示入口
 * @param params 请求参数（无参数）
 * @returns Promise<UseEntranceInfoResponse | undefined>
 */
export const queryUseEntranceInfo = async (
  params: UseEntranceInfoParams = {},
): Promise<UseEntranceInfoResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryUseEntranceInfo',
  params,
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 获取入口跳转地址
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405737
 * @description 获取分期商城入口跳转地址接口
 * @param params 请求参数
 * @returns Promise<EntranceUrlResponse | undefined>
 */
export const queryEntranceUrl = async (
  entranceType: number,
): Promise<EntranceUrlResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryEntranceUrl',
  { entranceType },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 发起授信申请接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/405761
 * @description 分期商城-发起授信申请接口
 * @param params 请求参数
 * @returns Promise<CreditMallApplyResponse | undefined>
 */
export const creditMallApply = async (
  params: {
    from: string,
    productIds: string[];
  },
): Promise<CreditMallApplyResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.credit.apply',
  { ...params },
  {
    showErrMsg: undefined,
  },
);

/**
 * 获取授信结果接口
 * @yapi https://yapi.hellobike.cn/project/7020/interface/api/418945
 * @description 获取授信结果接口
 * @param params 请求参数
 * @returns Promise<QueryCreditApplyStatusResponse | undefined>
 */
export const queryCreditApplyStatus = async (
  params: QueryCreditApplyStatusParams,
): Promise<QueryCreditApplyStatusResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.credit.queryCreditApplyStatus',
  {
    ...params,
  },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 资料审核页展示接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/424003
 * @description 资料审核页展示接口
 * @param params 请求参数（无参数）
 * @returns Promise<QueryAgreementReviewShowResponse | undefined>
 */
export const queryAgreementReviewShow = async (
): Promise<QueryAgreementReviewShowResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryAgreementReviewShow',
  {},
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 分期商城协议获取接口
 * @yapi https://yapi.hellobike.cn/project/7020/interface/api/418943
 * @description 分期商城协议获取接口
 * @param params 请求参数
 * @returns Promise<QueryContractListResponse | undefined>
 */
export const queryContractList = async (
  params: QueryContractListParams,
): Promise<QueryContractListResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryContractList',
  {
    ...params,
  },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 提现结果页分期商城概要信息查询接口
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/424568
 * @description 提现结果页分期商城概要信息查询接口，如果data为null，则不需要展示对应入口信息，异常需要捕获处理不展示入口
 * @param params 请求参数（无参数）
 * @returns Promise<WithdrawResultSummaryResponse | undefined>
 */
export const queryWithdrawResultSummary = async (
): Promise<WithdrawResultSummaryResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryWithdrawResultSummary',
  {},
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);

/**
 * 查询合同列表接口（支持多产品）
 * @yapi https://yapi.hellobike.cn/project/7647/interface/api/424580
 * @description 查询合同列表:一次可以查询多个产品的合同列表,合同列表平铺返回
 * @param params 请求参数
 * @returns Promise<QueryMultiContractListResponse | undefined>
 */
export const queryMultiContractList = async (
  params: QueryMultiContractListParams,
): Promise<QueryMultiContractListResponse | undefined> => requestDefault.postGate(
  'f.member.creditmall.entrance.queryMultiContractList',
  {
    ...params,
  },
  {
    showLoading: undefined,
    showErrMsg: undefined,
  },
);
